import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, ProgressBar, Alert, Table, Accordion
} from 'react-bootstrap';
import {
    UserCheck, Plus, CheckCircle, Circle, Clock,
    ChevronRight, Users, AlertCircle, X, Edit2,
    MessageSquare, User, Calendar, BookOpen
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Constants ────────────────────────────────────────────────────────────────
const HR_ROLES     = ['super-admin', 'admin', 'hr-manager', 'account-manager'];
const STAGES       = [
    { key: 'pre_joining', label: 'Pre-joining',  color: 'info',    icon: '📋', desc: 'Before Day 1' },
    { key: 'day_one',     label: 'Day 1',         color: 'primary', icon: '🌟', desc: 'First day tasks' },
    { key: 'first_week',  label: 'First Week',    color: 'warning', icon: '📅', desc: 'Days 2–7' },
    { key: 'first_month', label: 'First Month',   color: 'success', icon: '🎯', desc: 'Days 8–30' },
];
const CATEGORIES   = ['general','it_setup','documentation','hr_admin','team','training','benefits'];
const ASSIGN_ROLES = ['hr-manager','admin','employee','reporting-manager','it-admin'];
const STATUS_MAP   = {
    not_started: { bg: 'secondary', label: 'Not started' },
    in_progress: { bg: 'primary',   label: 'In progress' },
    completed:   { bg: 'success',   label: 'Completed'   },
};

const formatDate = (d) => {
    if (!d) return '—';
    const [y,m,dd] = String(d).slice(0,10).split('-').map(Number);
    return new Date(y,m-1,dd).toLocaleDateString('en-IN',{ day:'numeric', month:'short', year:'numeric' });
};

// ── Progress ring ────────────────────────────────────────────────────────────
const ProgressRing = ({ pct, size = 56, stroke = 5, color = '#4f46e5' }) => {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke}/>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
                strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round" style={{ transition: 'stroke-dashoffset .4s ease' }}/>
        </svg>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const OnboardingPage = () => {
    const { user } = useAuth();
    const isHR     = HR_ROLES.includes(user?.role);

    return isHR ? <HRView /> : <EmployeeView />;
};

// ════════════════════════════════════════════════════════════════════════════
// HR VIEW — manage templates and all instances
// ════════════════════════════════════════════════════════════════════════════
const HRView = () => {
    const [tab, setTab]             = useState('instances'); // 'instances' | 'templates'
    const [instances, setInstances] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [selected, setSelected]   = useState(null); // instance detail
    const [showStart, setShowStart] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/onboarding/all'),
            axiosInstance.get('/onboarding/templates'),
        ]).then(([i, t]) => {
            setInstances(i.data.data?.instances || []);
            setTemplates(t.data.data || []);
        }).catch(() => toast.error('Failed to load onboarding data'))
          .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (selected) return (
        <InstanceDetail instance={selected} onBack={() => setSelected(null)} onRefresh={load} />
    );

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <UserCheck size={22} className="me-2 text-primary"/>Onboarding
                    </h4>
                    <p className="text-muted small mb-0">
                        Manage new hire onboarding — {instances.length} active processes
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant={tab === 'instances' ? 'primary' : 'outline-secondary'}
                        size="sm" onClick={() => setTab('instances')}>
                        <Users size={14} className="me-1"/>All Employees
                    </Button>
                    <Button variant={tab === 'templates' ? 'primary' : 'outline-secondary'}
                        size="sm" onClick={() => setTab('templates')}>
                        <BookOpen size={14} className="me-1"/>Templates
                    </Button>
                    <Button variant="success" size="sm" onClick={() => setShowStart(true)}>
                        <Plus size={14} className="me-1"/>Start Onboarding
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : tab === 'instances' ? (
                <InstancesTable instances={instances} onSelect={setSelected} />
            ) : (
                <TemplatesManager templates={templates} onRefresh={load} />
            )}

            {showStart && (
                <StartOnboardingModal
                    templates={templates}
                    onClose={() => setShowStart(false)}
                    onDone={load}
                />
            )}
        </div>
    );
};

// ── Instances table ──────────────────────────────────────────────────────────
const InstancesTable = ({ instances, onSelect }) => {
    if (!instances.length) return (
        <Card className="border-0 shadow-sm p-5 text-center">
            <UserCheck size={40} className="mx-auto mb-3 text-muted opacity-50"/>
            <p className="text-muted">No onboarding processes yet. Start one for a new hire.</p>
        </Card>
    );
    return (
        <Card className="border-0 shadow-sm">
            <Table hover responsive className="mb-0 small align-middle">
                <thead className="bg-light">
                    <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                        <th className="ps-4">Employee</th>
                        <th>Template</th>
                        <th>Join Date</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th className="pe-4 text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {instances.map(i => (
                        <tr key={i.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(i)}>
                            <td className="ps-4">
                                <div className="fw-medium">{i.employee_name}</div>
                                <div className="text-muted" style={{ fontSize: 11 }}>{i.employee_code}</div>
                            </td>
                            <td>{i.template_name}</td>
                            <td>{formatDate(i.join_date)}</td>
                            <td style={{ minWidth: 140 }}>
                                <div className="d-flex align-items-center gap-2">
                                    <ProgressBar now={i.progress_pct || 0} style={{ flex: 1, height: 6 }}
                                        variant={i.progress_pct >= 100 ? 'success' : 'primary'}/>
                                    <span style={{ fontSize: 11, minWidth: 32 }}>{i.progress_pct || 0}%</span>
                                </div>
                                <div className="text-muted" style={{ fontSize: 10 }}>
                                    {i.completed_tasks}/{i.total_tasks} tasks
                                </div>
                            </td>
                            <td>
                                <Badge bg={STATUS_MAP[i.status]?.bg || 'secondary'}>
                                    {STATUS_MAP[i.status]?.label || i.status}
                                </Badge>
                            </td>
                            <td className="pe-4 text-end">
                                <Button size="sm" variant="outline-primary" className="py-0"
                                    onClick={e => { e.stopPropagation(); onSelect(i); }}>
                                    View <ChevronRight size={13}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Card>
    );
};

// ── Instance Detail ──────────────────────────────────────────────────────────
const InstanceDetail = ({ instance, onBack, onRefresh }) => {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [saving,  setSaving]  = useState(false);

    const load = () => {
        setLoading(true);
        axiosInstance.get(`/onboarding/${instance.id}`)
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load detail'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [instance.id]);

    const markComplete = async (itemId) => {
        try {
            await axiosInstance.patch(`/onboarding/items/${itemId}/complete`);
            toast.success('Task completed!');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const skipTask = async (itemId) => {
        try {
            await axiosInstance.patch(`/onboarding/items/${itemId}/skip`, { reason: 'Skipped by HR' });
            toast.success('Task skipped');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const addComment = async () => {
        if (!comment.trim()) return;
        setSaving(true);
        try {
            await axiosInstance.post(`/onboarding/${instance.id}/comment`, { comment });
            setComment('');
            load();
        } catch { toast.error('Failed to add comment'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (!data)   return null;

    const { progress } = data;

    return (
        <div className="p-2 p-md-4">
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>
                ← Back
            </Button>

            {/* Header */}
            <Card className="border-0 shadow-sm mb-4 p-4" style={{
                background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)' }}>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="fw-bold mb-1">{data.employee_name}</h5>
                        <div className="text-muted small mb-1">{data.employee_email}</div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg="light" text="dark">Join: {formatDate(data.join_date)}</Badge>
                            {data.buddy_name && (
                                <Badge bg="light" text="dark">
                                    <User size={11} className="me-1"/>Buddy: {data.buddy_name}
                                </Badge>
                            )}
                            <Badge bg={STATUS_MAP[data.status]?.bg || 'secondary'}>
                                {STATUS_MAP[data.status]?.label}
                            </Badge>
                        </div>
                    </Col>
                    <Col xs="auto" className="text-center">
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <ProgressRing pct={progress.percentage} size={72} stroke={6}/>
                            <div style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexDirection: 'column',
                            }}>
                                <span style={{ fontSize: 14, fontWeight: 700 }}>{progress.percentage}%</span>
                            </div>
                        </div>
                        <div className="text-muted small mt-1">{progress.done}/{progress.total} tasks</div>
                        {progress.overdue > 0 && (
                            <Badge bg="danger" className="mt-1">{progress.overdue} overdue</Badge>
                        )}
                    </Col>
                </Row>
            </Card>

            {/* Stage progress summary */}
            <Row className="g-3 mb-4">
                {STAGES.map(s => {
                    const stat = progress.by_stage?.[s.key] || { total: 0, done: 0, pct: 0 };
                    return (
                        <Col key={s.key} xs={6} lg={3}>
                            <Card className="border-0 shadow-sm p-3 text-center">
                                <div style={{ fontSize: 20 }}>{s.icon}</div>
                                <div className="fw-medium small">{s.label}</div>
                                <ProgressBar now={stat.pct} style={{ height: 4, margin: '6px 0' }}
                                    variant={s.color}/>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                    {stat.done}/{stat.total}
                                </div>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* Tasks by stage */}
            <Card className="border-0 shadow-sm mb-4">
                <Accordion defaultActiveKey="day_one">
                    {STAGES.map(s => {
                        const items = data.items_by_stage?.[s.key] || [];
                        if (!items.length) return null;
                        const done = items.filter(i => ['completed','skipped'].includes(i.status)).length;
                        return (
                            <Accordion.Item key={s.key} eventKey={s.key}>
                                <Accordion.Header>
                                    <span className="me-2">{s.icon}</span>
                                    <strong>{s.label}</strong>
                                    <Badge bg={s.color} className="ms-2 fw-normal">
                                        {done}/{items.length}
                                    </Badge>
                                    {items.some(i => i.status==='pending' && i.due_date && new Date(i.due_date)<new Date()) && (
                                        <Badge bg="danger" className="ms-1">Overdue</Badge>
                                    )}
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    {items.map(item => (
                                        <div key={item.id} className="d-flex align-items-center gap-3 px-4 py-3"
                                            style={{ borderBottom: '0.5px solid var(--bs-border-color)' }}>
                                            {item.status === 'completed'
                                                ? <CheckCircle size={18} className="text-success flex-shrink-0"/>
                                                : item.status === 'skipped'
                                                ? <X size={18} className="text-muted flex-shrink-0"/>
                                                : <Circle size={18} className="text-muted flex-shrink-0"/>
                                            }
                                            <div className="flex-grow-1">
                                                <div className="fw-medium small"
                                                    style={{ textDecoration: item.status==='skipped' ? 'line-through' : 'none' }}>
                                                    {item.title}
                                                    {item.is_required && <Badge bg="light" text="danger" className="ms-1" style={{ fontSize: 9 }}>Required</Badge>}
                                                </div>
                                                {item.description && (
                                                    <div className="text-muted" style={{ fontSize: 11 }}>{item.description}</div>
                                                )}
                                                <div className="d-flex gap-2 mt-1 flex-wrap">
                                                    {item.due_date && (
                                                        <span className="text-muted" style={{ fontSize: 10 }}>
                                                            <Calendar size={10} className="me-1"/>
                                                            Due {formatDate(item.due_date)}
                                                            {item.status==='pending' && new Date(item.due_date)<new Date() && (
                                                                <Badge bg="danger" className="ms-1" style={{ fontSize: 9 }}>Overdue</Badge>
                                                            )}
                                                        </span>
                                                    )}
                                                    <span className="text-muted" style={{ fontSize: 10 }}>
                                                        Assigned to: {item.assigned_to_role}
                                                    </span>
                                                    {item.completed_by_name && (
                                                        <span className="text-success" style={{ fontSize: 10 }}>
                                                            ✓ Done by {item.completed_by_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {item.status === 'pending' && (
                                                <div className="d-flex gap-1 flex-shrink-0">
                                                    <Button size="sm" variant="success" className="py-0 px-2"
                                                        onClick={() => markComplete(item.id)}>
                                                        <CheckCircle size={13}/> Done
                                                    </Button>
                                                    {!item.is_required && (
                                                        <Button size="sm" variant="outline-secondary" className="py-0 px-1"
                                                            onClick={() => skipTask(item.id)} title="Skip">
                                                            <X size={13}/>
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            </Card>

            {/* Comments */}
            <Card className="border-0 shadow-sm p-4">
                <h6 className="fw-bold mb-3"><MessageSquare size={16} className="me-1"/>Notes & Comments</h6>
                {data.comments?.map(c => (
                    <div key={c.id} className="mb-3 p-3 rounded"
                        style={{ background: 'var(--bs-light)', fontSize: 13 }}>
                        <div className="fw-medium">{c.author_name}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{formatDate(c.created_at)}</div>
                        <div className="mt-1">{c.comment}</div>
                    </div>
                ))}
                <div className="d-flex gap-2 mt-3">
                    <Form.Control as="textarea" rows={2} placeholder="Add a note…"
                        value={comment} onChange={e => setComment(e.target.value)}/>
                    <Button variant="primary" onClick={addComment} disabled={saving || !comment.trim()}
                        style={{ whiteSpace: 'nowrap' }}>
                        {saving ? <Spinner size="sm"/> : 'Add note'}
                    </Button>
                </div>
            </Card>
        </div>
    );
};

// ── Templates Manager ────────────────────────────────────────────────────────
const TemplatesManager = ({ templates, onRefresh }) => {
    const [editing,   setEditing]   = useState(null); // null | template
    const [showForm,  setShowForm]  = useState(false);
    const [tasks,     setTasks]     = useState([]);
    const [loadingT,  setLoadingT]  = useState(false);
    const [form,      setForm]      = useState({ name: '', description: '' });
    const [taskForm,  setTaskForm]  = useState({
        title: '', description: '', stage: 'day_one',
        assigned_to_role: 'hr-manager', due_day_offset: 0,
        is_required: true, category: 'general',
    });
    const [saving, setSaving] = useState(false);

    const openTemplate = async (tpl) => {
        setEditing(tpl);
        setLoadingT(true);
        try {
            const res = await axiosInstance.get(`/onboarding/templates/${tpl.id}/tasks`);
            setTasks(res.data.data || []);
        } finally { setLoadingT(false); }
    };

    const saveTemplate = async () => {
        if (!form.name.trim()) return toast.error('Template name required');
        setSaving(true);
        try {
            if (editing) {
                await axiosInstance.put(`/onboarding/templates/${editing.id}`, form);
                toast.success('Template updated');
            } else {
                await axiosInstance.post('/onboarding/templates', form);
                toast.success('Template created');
            }
            setShowForm(false);
            setForm({ name: '', description: '' });
            onRefresh();
        } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
        finally { setSaving(false); }
    };

    const addTask = async () => {
        if (!taskForm.title.trim()) return toast.error('Task title required');
        setSaving(true);
        try {
            await axiosInstance.post(`/onboarding/templates/${editing.id}/tasks`, taskForm);
            toast.success('Task added');
            setTaskForm({ title:'', description:'', stage:'day_one',
                assigned_to_role:'hr-manager', due_day_offset:0, is_required:true, category:'general' });
            openTemplate(editing);
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const deleteTask = async (taskId) => {
        try {
            await axiosInstance.delete(`/onboarding/templates/tasks/${taskId}`);
            toast.success('Task removed');
            openTemplate(editing);
        } catch { toast.error('Failed'); }
    };

    if (editing) return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3"
                onClick={() => { setEditing(null); setTasks([]); }}>← Templates</Button>
            <Card className="border-0 shadow-sm mb-4 p-4">
                <h5 className="fw-bold">{editing.name}</h5>
                <p className="text-muted small mb-0">{editing.description || 'No description'}</p>
            </Card>
            {/* Task list by stage */}
            {STAGES.map(s => {
                const stageTasks = tasks.filter(t => t.stage === s.key);
                return (
                    <Card key={s.key} className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white border-0 pt-3 fw-bold">
                            {s.icon} {s.label} — {s.desc}
                            <Badge bg={s.color} className="ms-2 fw-normal">{stageTasks.length} tasks</Badge>
                        </Card.Header>
                        {stageTasks.map(task => (
                            <div key={task.id} className="d-flex align-items-center px-4 py-2 gap-3"
                                style={{ borderTop: '0.5px solid var(--bs-border-color)' }}>
                                <div className="flex-grow-1">
                                    <div className="small fw-medium">{task.title}
                                        {task.is_required && <Badge bg="light" text="danger" className="ms-1" style={{fontSize:9}}>Required</Badge>}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                        Due: Day +{task.due_day_offset} · {task.assigned_to_role} · {task.category}
                                    </div>
                                </div>
                                <Button size="sm" variant="outline-danger" className="py-0"
                                    onClick={() => deleteTask(task.id)}><X size={12}/></Button>
                            </div>
                        ))}
                    </Card>
                );
            })}
            {/* Add task form */}
            <Card className="border-0 shadow-sm p-4">
                <h6 className="fw-bold mb-3"><Plus size={15} className="me-1"/>Add Task to Template</h6>
                <Row className="g-3">
                    <Col xs={12}><Form.Control placeholder="Task title *" value={taskForm.title}
                        onChange={e => setTaskForm(p => ({...p, title: e.target.value}))}/></Col>
                    <Col xs={12}><Form.Control placeholder="Description (optional)" value={taskForm.description}
                        onChange={e => setTaskForm(p => ({...p, description: e.target.value}))}/></Col>
                    <Col xs={6} md={3}>
                        <Form.Select value={taskForm.stage}
                            onChange={e => setTaskForm(p => ({...p, stage: e.target.value}))}>
                            {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={3}>
                        <Form.Select value={taskForm.assigned_to_role}
                            onChange={e => setTaskForm(p => ({...p, assigned_to_role: e.target.value}))}>
                            {ASSIGN_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Control type="number" placeholder="Due day" min={0} max={90}
                            value={taskForm.due_day_offset}
                            onChange={e => setTaskForm(p => ({...p, due_day_offset: +e.target.value}))}/>
                    </Col>
                    <Col xs={6} md={2}>
                        <Form.Select value={taskForm.category}
                            onChange={e => setTaskForm(p => ({...p, category: e.target.value}))}>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs={12} md={2}>
                        <Form.Check label="Required" checked={taskForm.is_required}
                            onChange={e => setTaskForm(p => ({...p, is_required: e.target.checked}))}/>
                    </Col>
                    <Col xs={12}>
                        <Button variant="primary" onClick={addTask} disabled={saving}>
                            {saving ? <Spinner size="sm"/> : <><Plus size={14} className="me-1"/>Add Task</>}
                        </Button>
                    </Col>
                </Row>
            </Card>
        </div>
    );

    return (
        <div>
            <div className="d-flex justify-content-end mb-3">
                <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setForm({ name:'', description:'' }); }}>
                    <Plus size={14} className="me-1"/>New Template
                </Button>
            </div>
            {templates.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <BookOpen size={36} className="mx-auto mb-2 opacity-50"/>
                    <p>No templates yet. Create one to start onboarding employees.</p>
                </Card>
            ) : (
                <Row className="g-3">
                    {templates.map(t => (
                        <Col key={t.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100" style={{ cursor: 'pointer' }}
                                onClick={() => openTemplate(t)}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="fw-bold mb-0">{t.name}</h6>
                                        <Badge bg={t.is_active ? 'success' : 'secondary'}>
                                            {t.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </div>
                                    <p className="text-muted small mb-3">{t.description || 'No description'}</p>
                                    <div className="text-muted small">
                                        <BookOpen size={12} className="me-1"/>{t.task_count} tasks
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            {/* Template form modal */}
            <Modal show={showForm} onHide={() => setShowForm(false)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">New Onboarding Template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Template Name *</Form.Label>
                            <Form.Control placeholder="e.g. Software Engineer Onboarding"
                                value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}/>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Description</Form.Label>
                            <Form.Control as="textarea" rows={2} placeholder="What is this template for?"
                                value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}/>
                        </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-4">
                        <Button variant="light" className="flex-grow-1" onClick={() => setShowForm(false)}>Cancel</Button>
                        <Button variant="primary" className="flex-grow-1" onClick={saveTemplate} disabled={saving}>
                            {saving ? <Spinner size="sm"/> : 'Create Template'}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

// ── Start Onboarding Modal ────────────────────────────────────────────────────
const StartOnboardingModal = ({ templates, onClose, onDone }) => {
    const [employees, setEmployees] = useState([]);
    const [form, setForm]           = useState({ employee_id:'', template_id:'', join_date:'', buddy_id:'', notes:'' });
    const [saving, setSaving]       = useState(false);

    useEffect(() => {
        axiosInstance.get('/employees')
            .then(r => setEmployees(r.data.data || []))
            .catch(() => {});
    }, []);

    const submit = async () => {
        if (!form.employee_id || !form.template_id)
            return toast.error('Employee and template are required');
        setSaving(true);
        try {
            await axiosInstance.post(`/onboarding/start/${form.employee_id}`, {
                template_id: form.template_id,
                join_date:   form.join_date || undefined,
                buddy_id:    form.buddy_id  || undefined,
                notes:       form.notes     || undefined,
            });
            toast.success('Onboarding started!');
            onClose();
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to start onboarding'); }
        finally { setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <UserCheck size={18} className="me-2 text-primary"/>Start Onboarding
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Employee *</Form.Label>
                        <Form.Select value={form.employee_id}
                            onChange={e => setForm(p => ({...p, employee_id: e.target.value}))}>
                            <option value="">— Select employee —</option>
                            {employees.map(e => (
                                <option key={e.id} value={e.id}>
                                    {e.first_name} {e.last_name} ({e.employee_code || e.email})
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Onboarding Template *</Form.Label>
                        <Form.Select value={form.template_id}
                            onChange={e => setForm(p => ({...p, template_id: e.target.value}))}>
                            <option value="">— Select template —</option>
                            {templates.filter(t => t.is_active).map(t => (
                                <option key={t.id} value={t.id}>{t.name} ({t.task_count} tasks)</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Join Date</Form.Label>
                        <Form.Control type="date" value={form.join_date}
                            onChange={e => setForm(p => ({...p, join_date: e.target.value}))}/>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Buddy (optional)</Form.Label>
                        <Form.Select value={form.buddy_id}
                            onChange={e => setForm(p => ({...p, buddy_id: e.target.value}))}>
                            <option value="">— No buddy —</option>
                            {employees.map(e => (
                                <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Notes</Form.Label>
                        <Form.Control as="textarea" rows={2} placeholder="Any special notes for this onboarding…"
                            value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="success" className="flex-grow-1 fw-bold" onClick={submit} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : '🚀 Start Onboarding'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// EMPLOYEE VIEW — new hire sees their own checklist
// ════════════════════════════════════════════════════════════════════════════
const EmployeeView = () => {
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/onboarding/my')
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load onboarding'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const markComplete = async (itemId) => {
        try {
            await axiosInstance.patch(`/onboarding/items/${itemId}/complete`);
            toast.success('Great job! Task completed ✓');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;

    if (!data) return (
        <div className="p-4 text-center">
            <UserCheck size={48} className="text-muted opacity-50 mb-3"/>
            <h5 className="text-muted">No onboarding checklist yet</h5>
            <p className="text-muted small">Your HR team will set this up when you join.</p>
        </div>
    );

    const { progress } = data;

    return (
        <div className="p-2 p-md-4">
            {/* Welcome card */}
            <Card className="border-0 shadow-sm mb-4 p-4"
                style={{ background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)' }}>
                <Row className="align-items-center">
                    <Col>
                        <h4 className="fw-bold mb-1">Welcome to {data.template_name} 🎉</h4>
                        <p className="text-muted mb-2">
                            Here's everything you need to do to get settled in.
                            Your buddy is {data.buddy_name || 'being assigned'}.
                        </p>
                        {progress.overdue > 0 && (
                            <Alert variant="warning" className="mb-0 py-2 small">
                                <AlertCircle size={14} className="me-1"/>
                                {progress.overdue} task{progress.overdue > 1 ? 's are' : ' is'} overdue — try to complete them today.
                            </Alert>
                        )}
                    </Col>
                    <Col xs="auto" className="text-center">
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <ProgressRing pct={progress.percentage} size={88} stroke={7}
                                color={progress.percentage >= 100 ? '#059669' : '#4f46e5'}/>
                            <div style={{ position:'absolute', inset:0, display:'flex',
                                alignItems:'center', justifyContent:'center', flexDirection:'column' }}>
                                <span style={{ fontSize: 18, fontWeight: 700 }}>{progress.percentage}%</span>
                                <span style={{ fontSize: 10, color: '#64748b' }}>done</span>
                            </div>
                        </div>
                        <div className="text-muted small mt-1">{progress.done}/{progress.total} tasks</div>
                    </Col>
                </Row>
            </Card>

            {/* Stage summary pills */}
            <div className="d-flex gap-2 flex-wrap mb-4">
                {STAGES.map(s => {
                    const stat = progress.by_stage?.[s.key] || { pct: 0 };
                    return (
                        <div key={s.key} className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                            style={{ background: '#f1f5f9', fontSize: 12 }}>
                            <span>{s.icon}</span>
                            <span className="fw-medium">{s.label}</span>
                            <Badge bg={stat.pct === 100 ? 'success' : s.color}>{stat.pct}%</Badge>
                        </div>
                    );
                })}
            </div>

            {/* Tasks by stage */}
            <Accordion defaultActiveKey="day_one">
                {STAGES.map(s => {
                    const items = data.items_by_stage?.[s.key] || [];
                    if (!items.length) return null;
                    const done = items.filter(i => ['completed','skipped'].includes(i.status)).length;
                    const allDone = done === items.length;
                    return (
                        <Card key={s.key} className="border-0 shadow-sm mb-3">
                            <Accordion.Item eventKey={s.key} className="border-0">
                                <Accordion.Header>
                                    <span className="me-2 fs-5">{s.icon}</span>
                                    <div>
                                        <div className="fw-bold">{s.label}
                                            {allDone && <CheckCircle size={15} className="ms-2 text-success"/>}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>{s.desc}</div>
                                    </div>
                                    <Badge bg={allDone ? 'success' : s.color} className="ms-3">
                                        {done}/{items.length}
                                    </Badge>
                                </Accordion.Header>
                                <Accordion.Body className="p-0">
                                    {items.map(item => (
                                        <div key={item.id} className="d-flex align-items-start gap-3 px-4 py-3"
                                            style={{ borderBottom: '0.5px solid #f1f5f9' }}>
                                            <div className="pt-1">
                                                {item.status === 'completed'
                                                    ? <CheckCircle size={20} className="text-success"/>
                                                    : item.status === 'skipped'
                                                    ? <X size={20} className="text-muted"/>
                                                    : <Circle size={20} className="text-muted"/>}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-medium"
                                                    style={{ textDecoration: item.status==='skipped'?'line-through':undefined }}>
                                                    {item.title}
                                                    {item.is_required && (
                                                        <span className="ms-1" style={{ fontSize: 10, color: '#dc2626' }}>*required</span>
                                                    )}
                                                </div>
                                                {item.description && (
                                                    <p className="text-muted small mb-1">{item.description}</p>
                                                )}
                                                {item.due_date && (
                                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                                        <Clock size={11} className="me-1"/>
                                                        Due {formatDate(item.due_date)}
                                                        {item.status==='pending' && new Date(item.due_date)<new Date() && (
                                                            <Badge bg="danger" className="ms-1" style={{fontSize:9}}>Overdue</Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {item.status === 'pending' && (
                                                <Button size="sm" variant="outline-success" className="py-1 flex-shrink-0"
                                                    onClick={() => markComplete(item.id)}>
                                                    Mark done
                                                </Button>
                                            )}
                                            {item.status === 'completed' && (
                                                <Badge bg="success" className="flex-shrink-0">Done ✓</Badge>
                                            )}
                                        </div>
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Card>
                    );
                })}
            </Accordion>
        </div>
    );
};

export default OnboardingPage;
